package com.example.mystybag.login.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.SoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.login.logic.StaffChangePasswordViewModel
import com.example.mystybag.login.views.views.StaffChangePasswordWebView
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun StaffChangePasswordView(viewModel: StaffChangePasswordViewModel) {
    if (PlatformHelper.isWeb) {
        StaffChangePasswordWebView(viewModel)
        return
    }
    val keyboardController = LocalSoftwareKeyboardController.current

    BottomSheetUIView(
        viewModel = viewModel,
        bottomSheetFactory = {}
    ) {
        StaffChangePasswordForm(
            viewModel = viewModel,
            keyboardController = keyboardController,
            useScrollAndFixedButtonSpacing = false
        )
    }
}

@Composable
internal fun ColumnScope.StaffChangePasswordForm(
    viewModel: StaffChangePasswordViewModel,
    keyboardController: SoftwareKeyboardController?,
    useScrollAndFixedButtonSpacing: Boolean
) {
    val uiState by viewModel.uiState.subscribeAsState()

    val columnModifier = if (useScrollAndFixedButtonSpacing) {
        Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = p16)
    } else {
        Modifier
            .fillMaxWidth()
            .weight(1f)
    }

    Column(
        modifier = columnModifier,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val rowWidth = if (useScrollAndFixedButtonSpacing) Modifier.fillMaxWidth() else Modifier.fillMaxWidth(0.9f)

        Row(
            modifier = Modifier.padding(top = p24).then(rowWidth),
            horizontalArrangement = Arrangement.spacedBy(p6, alignment = Alignment.CenterHorizontally)
        ) {
            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.newPassword,
                label = stringResource(SR.strings.new_password),
                errorText = uiState.newPasswordErrorMsg,
                errorLeadingIcon = SRC.images.password_error_icon,
                onValueChange = viewModel::onNewPasswordChange,
                isError = uiState.newPasswordIsError,
                visualTransformation = if (uiState.newPasswordVisible) {
                    VisualTransformation.None
                } else {
                    PasswordVisualTransformation()
                },
                trailingIcon = {
                    Image(
                        painter = painterResource(
                            if (uiState.newPasswordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                        ),
                        modifier = Modifier.size(p24).noRippleClickable { viewModel.toggleNewPasswordVisibility() }
                    )
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Ascii, imeAction = ImeAction.Next)
            )
        }

        Row(
            modifier = Modifier.padding(top = p16).then(rowWidth),
            horizontalArrangement = Arrangement.spacedBy(p6, alignment = Alignment.CenterHorizontally)
        ) {
            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.confirmPassword,
                label = stringResource(SR.strings.confirm_password),
                errorText = uiState.confirmPasswordErrorMsg,
                errorLeadingIcon = SRC.images.password_error_icon,
                onValueChange = viewModel::onConfirmPasswordChange,
                isError = uiState.confirmPasswordIsError,
                visualTransformation = if (uiState.confirmPasswordVisible) {
                    VisualTransformation.None
                } else {
                    PasswordVisualTransformation()
                },
                trailingIcon = {
                    Image(
                        painter = painterResource(
                            if (uiState.confirmPasswordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                        ),
                        modifier = Modifier.size(p24).noRippleClickable { viewModel.toggleConfirmPasswordVisibility() }
                    )
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Ascii, imeAction = ImeAction.Done)
            )
        }

        if (useScrollAndFixedButtonSpacing) {
            Spacer(modifier = Modifier.height(p32))
        } else {
            Spacer(Modifier.weight(1f))
        }

        DefaultButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = p16),
            caption = stringResource(SR.strings.continue_camel_case),
            callback = {
                keyboardController?.hide()
                viewModel.onContinue()
            },
            identifier = "Staff Change Password Continue"
        )
    }
}
